/**
=========================================================
* Material Dashboard 2  React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useState, useEffect } from "react";
import axios from "axios";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// ReportsLineChart configurations
import configs from "examples/Charts/LineCharts/ReportsLineChart/configs";

// API Configuration
import API_BASE_URL from "config/api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function ReportsLineChart({ color, title, description, date, chart, username }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [chartData, setChartData] = useState(chart);
  const [timeframe, setTimeframe] = useState("All");

  const { data, options } = configs(chartData.labels || [], chartData.datasets || {});

  const openFullscreen = () => setFullscreen(true);
  const closeFullscreen = () => {
    setFullscreen(false);
    setChartData(chart);
    setTimeframe("All");
  };

  const handleTimeframeChange = async (newTimeframe) => {
    setTimeframe(newTimeframe);

    if (newTimeframe === "All") {
      setChartData(chart); // Revert to original chart data (all time)
      return;
    }

    try {
      let url = `${API_BASE_URL}/activity/quiz-scores`;
      const params = new URLSearchParams();
      params.append("timeframe", newTimeframe);
      // If username is available, append it to the params
      if (username) {
        params.append("username", username);
      }
      url = `${url}?${params.toString()}`;

      const response = await axios.get(url);
      const fetchedScores = response.data.data;

      const newChartData = {
        labels: fetchedScores.map((s) => new Date(s.timestamp).toLocaleDateString()),
        datasets: {
          label: "Quiz Score",
          data: fetchedScores.map((s) => s.score),
        },
      };
      setChartData(newChartData);
    } catch (error) {
      console.error("Error fetching timeframe data:", error);
    }
  };

  const renderChart = (isFullscreen) => {
    const chartContent = (
      <MDBox
        variant="gradient"
        bgColor={color}
        borderRadius="lg"
        coloredShadow={color}
        py={2}
        pr={0.5}
        mt={-5}
        height={isFullscreen ? "100%" : undefined}
      >
        <Line data={data} options={{ ...options, maintainAspectRatio: false }} redraw />
      </MDBox>
    );

    if (isFullscreen) {
      return chartContent;
    }

    return (
      <MDBox padding="1rem">
        {chartContent}
        <MDBox pt={3} pb={1} px={1}>
          <MDTypography variant="h6" textTransform="capitalize">
            {title}
          </MDTypography>
          <MDTypography component="div" variant="button" color="text" fontWeight="light">
            {description}
          </MDTypography>
          <Divider />
          <MDBox display="flex" alignItems="center">
            <MDTypography variant="button" color="text" lineHeight={1} sx={{ mt: 0.15, mr: 0.5 }}>
              <Icon>schedule</Icon>
            </MDTypography>
            <MDTypography variant="button" color="text" fontWeight="light">
              {date}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    );
  };

  return (
    <>
      <Card sx={{ height: "100%" }}>
        <MDBox display="flex" justifyContent="flex-end" pt={1} px={1}>
          <IconButton onClick={openFullscreen}>
            <Icon>fullscreen</Icon>
          </IconButton>
        </MDBox>
        {renderChart(false)}
      </Card>
      <Modal open={fullscreen} onClose={closeFullscreen}>
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            height: "90%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDTypography variant="h6">{title}</MDTypography>
            <ButtonGroup aria-label="outlined primary button group">
              <Button
                onClick={() => handleTimeframeChange("1D")}
                sx={{
                  backgroundColor: timeframe === "1D" ? "info.main" : "white",
                  color: timeframe === "1D" ? "white" : "grey.700",
                  "&:hover": {
                    backgroundColor: timeframe === "1D" ? "info.main" : "grey.200",
                  },
                }}
              >
                1D
              </Button>
              <Button
                onClick={() => handleTimeframeChange("1W")}
                sx={{
                  backgroundColor: timeframe === "1W" ? "info.main" : "white",
                  color: timeframe === "1W" ? "white" : "grey.700",
                  "&:hover": {
                    backgroundColor: timeframe === "1W" ? "info.main" : "grey.200",
                  },
                }}
              >
                1W
              </Button>
              <Button
                onClick={() => handleTimeframeChange("1M")}
                sx={{
                  backgroundColor: timeframe === "1M" ? "info.main" : "white",
                  color: timeframe === "1M" ? "white" : "grey.700",
                  "&:hover": {
                    backgroundColor: timeframe === "1M" ? "info.main" : "grey.200",
                  },
                }}
              >
                1M
              </Button>
              <Button
                onClick={() => handleTimeframeChange("1Y")}
                sx={{
                  backgroundColor: timeframe === "1Y" ? "info.main" : "white",
                  color: timeframe === "1Y" ? "white" : "grey.700",
                  "&:hover": {
                    backgroundColor: timeframe === "1Y" ? "info.main" : "grey.200",
                  },
                }}
              >
                1Y
              </Button>
              <Button
                onClick={() => handleTimeframeChange("All")}
                sx={{
                  backgroundColor: timeframe === "All" ? "info.main" : "white",
                  color: timeframe === "All" ? "white" : "grey.700",
                  "&:hover": {
                    backgroundColor: timeframe === "All" ? "info.main" : "grey.200",
                  },
                }}
              >
                All
              </Button>
            </ButtonGroup>
            <IconButton onClick={closeFullscreen}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
          <MDBox flexGrow={1} mt={4}>
            {renderChart(true)}
          </MDBox>
        </MDBox>
      </Modal>
    </>
  );
}

// Setting default values for the props of ReportsLineChart
ReportsLineChart.defaultProps = {
  color: "info",
  description: "",
};

// Typechecking props for the ReportsLineChart
ReportsLineChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  date: PropTypes.string.isRequired,
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  username: PropTypes.string,
};

export default ReportsLineChart;
